import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {MultiSelect} from "primereact/multiselect";
import {ProgressBar} from "primereact/progressbar";
import {RadioButton} from "primereact/radiobutton";
import {SelectButton} from "primereact/selectbutton";
import {useTranslate} from "../../system/useTranslate";
import {setMessage, setWorkspace} from "../../system/app-slice";
import {farsiDigitsToInt} from "../../system/utils";
import PrimeDatePicker from "../../system/components/prime-date-picker";
import BrandLogo from "../../system/components/brand-logo";
import {
    findPlanPriceForInterval,
    projectSubscriptionCatalog,
    SubscriptionPlanSelector,
} from "../../system/components/subscription-plan-selector";
import Project from "../../models/project/project";
import Goal from "../../models/goal/goal";
import WorkspaceInvitation from "../../models/workspace-invitation/workspace-invitation";
import {useWorkspace} from "../../models/workspace/use-workspace";
import useWorkspaceMembership from "../../models/workspace-membership/use-workspace-membership";
import {useCommerceCheckout} from "../../models/commerce-checkout/use-commerce-checkout";
import {usePayment} from "../../models/payment/use-payment";
import {
    activePlanCustomizations,
    customizationPayload,
    customizationSelectionsComplete,
    defaultCustomizationSelections,
} from "../../system/plan-customizations";
import {
    clearWorkspaceOnboardingSession,
    loadWorkspaceOnboardingSession,
    saveWorkspaceOnboardingSession,
} from "./onboarding-session";

const PURPOSE_OPTIONS = ["personal", "projects", "team"];
const OWNER_TYPE_OPTIONS = ["manager", "team-lead", "technical-expert"];
const INDUSTRY_OPTIONS = [
    "tech-software",
    "commerce",
    "health-beauty-service",
    "enterprise-management",
    "education-media",
    "other",
];
const START_OPTIONS = ["project", "goal"];
const INVITE_ROLE_OPTIONS = ["viewer", "guest"];

function emptyInvite() {
    return {fullname: "", phonenumber: "", role: "viewer"};
}

function createIdempotencyKey() {
    if (window.crypto?.randomUUID) return `workspace-onboarding:${window.crypto.randomUUID()}`;
    return `workspace-onboarding:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

function translatedError(error, translate) {
    const key = error?.message || error?.data?.error?.message || "workspace-onboarding.error.submit";
    const translated = translate(key);
    return translated === key ? translate("workspace-onboarding.error.submit") : translated;
}

function formatMoney(value, currency, translate) {
    if (value === null || value === undefined || value === "") return translate("billing.value.contractual");
    const amount = Number(String(value ?? 0));
    const formatted = Number.isFinite(amount) ? new Intl.NumberFormat().format(amount) : String(value ?? 0);
    const currencyKey = `commerce.currency.${String(currency || "IRR").toLowerCase()}`;
    const translatedCurrency = translate(currencyKey);
    return `${formatted} ${translatedCurrency === currencyKey ? currency : translatedCurrency}`;
}

function OptionChoice({id, name, value, selected, title, description, icon, layout = "row", onChange}) {
    const isTile = layout === "tile";
    return (
        <label htmlFor={id} className={`w-full flex ${isTile ? "flex-column justify-content-center text-center py-6 px-4" : "align-items-center p-4"} gap-3 border-1 border-round-xl cursor-pointer surface-0 ${selected ? "border-primary" : "border-200"}`}>
            <span className={`flex align-items-center justify-content-center border-1 border-circle border-200 ${isTile ? "w-4rem h-4rem" : "w-3rem h-3rem"}`}>
                <i className={`${icon} ${isTile ? "text-3xl" : "text-xl"} ${selected ? "text-primary" : "text-500"}`}/>
            </span>
            <span className="flex-1 flex flex-column gap-2">
                <span className={`${isTile ? "text-xl" : "text-base"} font-semibold text-900`}>{title}</span>
                {description && <span className="text-xs text-500 line-height-3">{description}</span>}
            </span>
            <RadioButton inputId={id} name={name} value={value} checked={selected} onChange={() => onChange(value)}/>
        </label>
    );
}

function StepContent({icon, title, description, children}) {
    return (
        <div className="w-full flex flex-column align-items-center gap-5">
            <div className="w-4rem h-4rem border-1 border-200 border-circle flex align-items-center justify-content-center">
                <i className={`${icon} text-3xl text-primary`}/>
            </div>
            <div className="text-center flex flex-column gap-3">
                <h2 className="m-0 text-3xl text-900">{title}</h2>
                {description && <span className="text-xs text-500 line-height-3">{description}</span>}
            </div>
            <div className="w-full">{children}</div>
        </div>
    );
}

function WizardNavigation({canGoBack, onBack, nextLabel, nextDisabled, loading}) {
    const translate = useTranslate();
    return (
        <div className="w-full flex flex-column-reverse sm:flex-row align-items-center justify-content-between gap-3 pt-4 border-top-1 border-100">
            {canGoBack ? (
                <Button type="button" className="w-full sm:w-auto text-xs gap-2 px-4 py-3" icon="pi pi-arrow-right" label={translate("workspace-onboarding.action.back")} severity="secondary" text onClick={onBack} disabled={loading}/>
            ) : <span/>}
            <Button type="submit" className="w-full sm:w-auto text-xs gap-2 px-6 py-3" icon="pi pi-arrow-left" iconPos="right" label={nextLabel || translate("workspace-onboarding.action.continue")} disabled={nextDisabled || loading} loading={loading}/>
        </div>
    );
}

export function WorkspaceOnboardingWizard({onCreated, welcomeStep = true, closable = false, onClose}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const {user} = useSelector((state) => state.auth);
    const workspace = useWorkspace();
    const workspaceMembership = useWorkspaceMembership();
    const {getQuote, checkout, quoteLoading, checkoutLoading} = useCommerceCheckout();
    const {requestZarinpalPayment, zarinpalLoading} = usePayment();
    const [restoredSession] = useState(() => loadWorkspaceOnboardingSession(user?.id));
    const pendingSessionRef = useRef(restoredSession);
    const resumeAttempted = useRef(false);
    const actionLockRef = useRef(false);
    const provisioned = useRef({
        starter: Boolean(restoredSession?.starterProvisioned),
        invites: new Set(restoredSession?.provisionedInviteIndexes || []),
    });
    const [stepIndex, setStepIndex] = useState(0);
    const [eligibility, setEligibility] = useState(null);
    const [eligibilityLoading, setEligibilityLoading] = useState(true);
    const [eligibilityError, setEligibilityError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [subscriptionInterval, setSubscriptionInterval] = useState("");
    const [createdWorkspace, setCreatedWorkspace] = useState(() => restoredSession ? {
        workspace: restoredSession.workspace,
        membership: restoredSession.membership,
        subscriptionMode: "professional",
    } : null);
    const [quote, setQuote] = useState(null);
    const [idempotencyKey] = useState(() => restoredSession?.retryCheckout ? createIdempotencyKey() : restoredSession?.idempotencyKey || createIdempotencyKey());
    const [data, setData] = useState(() => ({
        workspaceName: "",
        purpose: "projects",
        ownerType: "manager",
        workspaceCategory: "tech-software",
        starterType: "project",
        starterTitle: "",
        starterDueDate: null,
        invites: [emptyInvite(), emptyInvite(), emptyInvite()],
        ...(restoredSession?.form || {}),
        selectedPlan: null,
        selectedPrice: null,
        selectedCustomizations: restoredSession?.form?.selectedCustomizations || {},
    }));
    const subscriptionPlans = useMemo(() => projectSubscriptionCatalog([
        ...(eligibility?.freeSubscriptionEligible && eligibility?.freePlan ? [eligibility.freePlan] : []),
        ...(eligibility?.professionalPlans || []),
    ]).plans, [eligibility?.freePlan, eligibility?.freeSubscriptionEligible, eligibility?.professionalPlans]);
    const selectedSubscriptionPlan = useMemo(
        () => subscriptionPlans.find((plan) => (
            plan.planPrice?.documentId === data.selectedPrice?.documentId
            || (
                plan.planPrice?.id != null
                && data.selectedPrice?.id != null
                && String(plan.planPrice.id) === String(data.selectedPrice.id)
            )
        )) || null,
        [data.selectedPrice, subscriptionPlans]
    );
    const freeSubscriptionSelected = eligibility?.freeSubscriptionEligible
        && data.selectedPrice?.billingType === "free";
    const professionalSubscriptionSelected = Boolean(data.selectedPrice) && !freeSubscriptionSelected;

    const loadEligibility = async () => {
        setEligibilityLoading(true);
        setEligibilityError(null);
        try {
            const result = await workspace.getOnboardingEligibility();
            if (!result?.requiresProfessionalSubscription && !result?.freePlan) {
                setEligibility(null);
                setEligibilityError(translate("workspace-onboarding.error.free-plan-unavailable"));
                return;
            }
            setEligibility(result);
            const availablePlans = [
                ...(result?.freeSubscriptionEligible && result?.freePlan ? [result.freePlan] : []),
                ...(result?.professionalPlans || []),
            ];
            const restoredPlan = availablePlans.find((plan) => plan.documentId === restoredSession?.subscriptionPlanDocumentId);
            const restoredPrice = restoredPlan?.prices?.find((price) => price.documentId === restoredSession?.planPriceDocumentId);
            const initialPlan = restoredPlan
                || (result?.freeSubscriptionEligible ? result?.freePlan : null)
                || result?.professionalPlans?.find((plan) => plan.recommended)
                || result?.professionalPlans?.[0]
                || null;
            const initialInterval = restoredPrice
                ? `${restoredPrice.billingInterval || "month"}:${Number(restoredPrice.intervalCount || 1)}`
                : projectSubscriptionCatalog(result?.professionalPlans || []).plans.find((plan) => plan.billingType === "recurring")?.periodKey || "";
            if (initialInterval) setSubscriptionInterval(initialInterval);
            setData((current) => ({
                ...current,
                selectedPlan: initialPlan || current.selectedPlan,
                selectedPrice: restoredPrice
                    || current.selectedPrice
                    || findPlanPriceForInterval(initialPlan, initialInterval),
                selectedCustomizations: Object.keys(current.selectedCustomizations || {}).length
                    ? current.selectedCustomizations
                    : defaultCustomizationSelections(initialPlan),
            }));
        } catch (error) {
            setEligibilityError(translatedError(error, translate));
        } finally {
            setEligibilityLoading(false);
        }
    };

    useEffect(() => {
        loadEligibility();
        // Eligibility is loaded once for this wizard instance and explicitly retried by the user.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateInvite = (index, patch) => {
        setData((current) => ({
            ...current,
            invites: current.invites.map((invite, inviteIndex) => inviteIndex === index ? {...invite, ...patch} : invite),
        }));
    };

    const activeInvites = data.invites.filter((invite) => invite.phonenumber.trim());

    const changeSubscriptionInterval = (interval) => {
        if (!interval || interval === subscriptionInterval) return;
        setSubscriptionInterval(interval);
        setSubmitError(null);
        setData((current) => ({
            ...current,
            selectedPrice: findPlanPriceForInterval(current.selectedPlan, interval),
        }));
    };

    const selectSubscriptionPlan = (plan) => {
        setSubmitError(null);
        setData((current) => ({
            ...current,
            selectedPlan: plan.subscriptionPlan,
            selectedPrice: plan.planPrice,
            selectedCustomizations: defaultCustomizationSelections(plan),
        }));
    };

    const persistProfessionalSession = (result, patch = {}) => {
        if (!professionalSubscriptionSelected || !result?.workspace?.documentId) return null;
        const session = saveWorkspaceOnboardingSession({
            ...(pendingSessionRef.current || {}),
            userId: user?.id,
            workspace: {
                id: result.workspace.id,
                documentId: result.workspace.documentId,
                name: result.workspace.name,
            },
            membership: result.membership ? {
                id: result.membership.id,
                documentId: result.membership.documentId,
            } : pendingSessionRef.current?.membership,
            subscriptionPlanDocumentId: data.selectedPlan?.documentId,
            planPriceDocumentId: data.selectedPrice?.documentId,
            idempotencyKey,
            retryCheckout: false,
            starterProvisioned: provisioned.current.starter,
            provisionedInviteIndexes: [...provisioned.current.invites],
            form: {
                workspaceName: data.workspaceName,
                purpose: data.purpose,
                ownerType: data.ownerType,
                workspaceCategory: data.workspaceCategory,
                starterType: data.starterType,
                starterTitle: data.starterTitle,
                starterDueDate: data.starterDueDate,
                invites: data.invites,
                selectedCustomizations: data.selectedCustomizations,
            },
            ...patch,
        });
        pendingSessionRef.current = session;
        return session;
    };

    const createStarter = async (result) => {
        if (provisioned.current.starter) return;
        const workspaceData = result.workspace;
        const payload = {
            title: data.starterTitle.trim(),
            workspace: workspaceData.id,
            owner: result.membership?.documentId || result.membership?.id,
            ...(data.starterDueDate ? {dueDate: new Date(data.starterDueDate * 1000).toISOString()} : {}),
        };

        if (data.starterType === "goal") await new Goal().createItem(payload);
        else await new Project().createItem({
            ...payload,
            creatorUser: result.membership?.documentId || result.membership?.id,
        });
        provisioned.current.starter = true;
        persistProfessionalSession(result);
    };

    const sendInvitations = async (result) => {
        const workspaceData = result.workspace;
        const invitationModel = new WorkspaceInvitation();
        for (let index = 0; index < data.invites.length; index += 1) {
            const invite = data.invites[index];
            if (!invite.phonenumber.trim() || provisioned.current.invites.has(index)) continue;
            await invitationModel.invite({
                workspaceId: workspaceData.id,
                fullname: invite.fullname.trim(),
                phonenumber: invite.phonenumber.trim(),
                role: invite.role,
            }, workspaceData.documentId);
            provisioned.current.invites.add(index);
            persistProfessionalSession(result);
        }
    };

    const finishMembershipBootstrap = async (result) => {
        const memberships = await workspaceMembership.getWorkspaceMemberships();
        if (!memberships) throw new Error("workspace-onboarding.error.bootstrap");
        clearWorkspaceOnboardingSession(result?.workspace?.documentId);
        dispatch(setMessage({severity: "success", content: translate("workspace-onboarding.success")}));
        onCreated?.(result);
    };

    const provisionWorkspace = async () => {
        if (actionLockRef.current) return;
        actionLockRef.current = true;
        setSubmitting(true);
        setSubmitError(null);
        try {
            let result = createdWorkspace;
            if (!result) {
                result = await workspace.createWorkspace({
                    workspace: {
                        name: data.workspaceName.trim(),
                        ownerType: data.ownerType,
                        workspaceCategory: data.workspaceCategory,
                    },
                    ...(professionalSubscriptionSelected ? {subscriptionPlanPriceDocumentId: data.selectedPrice?.documentId} : {}),
                }, {refreshMemberships: false});
                if (!result?.workspace?.documentId || !result?.membership?.id) {
                    throw new Error("workspace-onboarding.error.bootstrap");
                }
                setCreatedWorkspace(result);
                dispatch(setWorkspace(result.workspace.documentId));
                persistProfessionalSession(result);
            }

            if (professionalSubscriptionSelected) {
                if (pendingSessionRef.current?.paymentCompleted) {
                    await createStarter(result);
                    await sendInvitations(result);
                    await finishMembershipBootstrap(result);
                    return;
                }
                const serverQuote = await getQuote({
                    planPrice: data.selectedPrice,
                    customizations: customizationPayload(selectedSubscriptionPlan || data.selectedPlan, data.selectedCustomizations),
                });
                setQuote(serverQuote);
                return;
            }

            await createStarter(result);
            await sendInvitations(result);
            await finishMembershipBootstrap(result);
        } catch (error) {
            setSubmitError(translatedError(error, translate));
        } finally {
            actionLockRef.current = false;
            setSubmitting(false);
        }
    };

    const deferProfessionalPayment = async () => {
        if (actionLockRef.current) return;
        actionLockRef.current = true;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const result = await workspace.createWorkspace({
                workspace: {
                    name: data.workspaceName.trim(),
                    ownerType: data.ownerType,
                    workspaceCategory: data.workspaceCategory,
                },
                subscriptionPlanPriceDocumentId: data.selectedPrice?.documentId,
                deferProfessionalPayment: true,
            }, {refreshMemberships: false});
            if (!result?.workspace?.documentId || !result?.membership?.id) {
                throw new Error("workspace-onboarding.error.bootstrap");
            }
            setCreatedWorkspace(result);
            dispatch(setWorkspace(result.workspace.documentId));
            await finishMembershipBootstrap(result);
        } catch (error) {
            setSubmitError(translatedError(error, translate));
        } finally {
            actionLockRef.current = false;
            setSubmitting(false);
        }
    };

    const completeProfessionalCheckout = async () => {
        if (actionLockRef.current || !quote || !data.selectedPrice || !createdWorkspace?.workspace?.documentId) return;
        actionLockRef.current = true;
        setSubmitError(null);
        try {
            dispatch(setWorkspace(createdWorkspace.workspace.documentId));
            const result = await checkout({
                planPrice: data.selectedPrice,
                customizations: customizationPayload(selectedSubscriptionPlan || data.selectedPlan, data.selectedCustomizations),
                idempotencyKey,
            });
            if (result?.requiresPayment) {
                if (!result.payment?.documentId) throw new Error("commerce.error.checkout-incomplete");
                const gateway = await requestZarinpalPayment(result.payment.documentId);
                if (gateway?.state === "success") {
                    persistProfessionalSession(createdWorkspace, {paymentCompleted: true});
                    await createStarter(createdWorkspace);
                    await sendInvitations(createdWorkspace);
                    await finishMembershipBootstrap(createdWorkspace);
                    return;
                }
                if (!gateway?.redirectUrl) throw new Error("payment.error.redirect-unavailable");
                persistProfessionalSession(createdWorkspace, {
                    paymentDocumentId: result.payment.documentId,
                    authority: gateway.authority,
                });
                window.location.assign(gateway.redirectUrl);
                return;
            }
            if (result && !result.requiresPayment) {
                persistProfessionalSession(createdWorkspace, {paymentCompleted: true});
                await createStarter(createdWorkspace);
                await sendInvitations(createdWorkspace);
                await finishMembershipBootstrap(createdWorkspace);
                return;
            }
            throw new Error("commerce.error.checkout-incomplete");
        } catch (error) {
            setSubmitError(translatedError(error, translate));
        } finally {
            actionLockRef.current = false;
        }
    };

    useEffect(() => {
        if (!restoredSession || resumeAttempted.current || eligibilityLoading || !eligibility || quote) return;
        if (!createdWorkspace?.workspace?.documentId || !professionalSubscriptionSelected || !data.selectedPrice) return;
        resumeAttempted.current = true;
        dispatch(setWorkspace(createdWorkspace.workspace.documentId));
        provisionWorkspace();
        // Resume is attempted once for the durable pending workspace/session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdWorkspace?.workspace?.documentId, data.selectedPrice, eligibility, eligibilityLoading, professionalSubscriptionSelected, quote, restoredSession]);

    const steps = useMemo(() => {
        const result = [];
        if (welcomeStep) result.push("welcome");
        result.push("workspace", "purpose", "owner-type", "industry", "start", "starter-settings");
        if (eligibility?.requiresProfessionalSubscription || eligibility?.freeSubscriptionEligible) result.push("subscription");
        result.push("invite");
        return result;
    }, [eligibility?.freeSubscriptionEligible, eligibility?.requiresProfessionalSubscription, welcomeStep]);

    const currentStep = steps[stepIndex] || steps[0];
    const displayStep = quote ? steps.length + 1 : stepIndex + 1;
    const totalSteps = steps.length + (quote ? 1 : 0);
    const busy = submitting || quoteLoading || checkoutLoading || zarinpalLoading;
    const goNext = () => setStepIndex((current) => Math.min(current + 1, steps.length - 1));
    const goBack = () => setStepIndex((current) => Math.max(current - 1, 0));
    const handleWizardSubmit = (event) => {
        event.preventDefault();
        if (busy || actionLockRef.current) return;
        if (currentStep === "workspace" && data.workspaceName.trim().length < 2) return;
        if (currentStep === "starter-settings" && data.starterTitle.trim().length < 2) return;
        if (currentStep === "subscription" && (
            !data.selectedPrice
            || !customizationSelectionsComplete(selectedSubscriptionPlan || data.selectedPlan, data.selectedCustomizations)
        )) return;
        if (quote) return completeProfessionalCheckout();
        if (currentStep === "invite") return provisionWorkspace();
        goNext();
    };

    if (eligibilityLoading) {
        return <div className="w-full h-full flex align-items-center justify-content-center"><i className="pi pi-spin pi-spinner text-3xl text-primary"/></div>;
    }

    if (eligibilityError || !eligibility) {
        return (
            <div className="w-full flex flex-column align-items-center gap-3 p-4 rtl">
                <Message className="w-full text-xs" severity="error" text={eligibilityError || translate("workspace-onboarding.error.eligibility")}/>
                <Button type="button" className="text-xs gap-2" icon="pi pi-refresh" label={translate("workspace-onboarding.action.retry")} onClick={loadEligibility}/>
            </div>
        );
    }

    const renderStep = () => {
        if (quote) {
            return (
                <StepContent icon="pi pi-credit-card" title={translate("workspace-onboarding.payment.title")} description={translate("workspace-onboarding.payment.description")}>
                    <div className="flex flex-column gap-3">
                        <div className="surface-50 border-1 border-200 border-round-xl p-3 flex flex-column gap-2">
                            <div className="flex justify-content-between gap-3 text-xs"><span>{translate("workspace-onboarding.subscription.plan")}</span><strong>{quote.plan?.name || data.selectedPlan.name}</strong></div>
                            <div className="flex justify-content-between gap-3 text-xs"><span>{translate("workspace-onboarding.subscription.subtotal")}</span><strong>{formatMoney(quote.amounts?.subtotal, quote.amounts?.currency, translate)}</strong></div>
                            <div className="flex justify-content-between gap-3 text-xs"><span>{translate("workspace-onboarding.subscription.discount")}</span><strong>{formatMoney(quote.amounts?.discount, quote.amounts?.currency, translate)}</strong></div>
                            <div className="flex justify-content-between gap-3 text-sm border-top-1 border-200 pt-2"><span>{translate("workspace-onboarding.subscription.payable")}</span><strong className="text-primary">{formatMoney(quote.amounts?.total, quote.amounts?.currency, translate)}</strong></div>
                        </div>
                        {submitError && <Message className="w-full text-xs" severity="error" text={submitError}/>}
                        <WizardNavigation canGoBack={false} nextLabel={translate("workspace-onboarding.action.pay")} onNext={completeProfessionalCheckout} loading={busy}/>
                    </div>
                </StepContent>
            );
        }

        switch (currentStep) {
            case "welcome":
                return (
                    <StepContent icon="pi pi-sparkles" title={translate("workspace-onboarding.welcome.title")} description={translate("workspace-onboarding.welcome.description")}>
                        <WizardNavigation canGoBack={false} nextLabel={translate("workspace-onboarding.action.start")} onNext={goNext}/>
                    </StepContent>
                );
            case "workspace":
                return (
                    <StepContent icon="pi pi-building" title={translate("workspace-onboarding.workspace.title")} description={translate("workspace-onboarding.workspace.description")}>
                        <div className="flex flex-column gap-4">
                            <InputText className="w-full text-sm p-3" value={data.workspaceName} onChange={(event) => setData({...data, workspaceName: event.target.value})} placeholder={translate("workspace-onboarding.workspace.placeholder")} autoFocus/>
                            {eligibility.freeSubscriptionEligible && <Message className="w-full text-xs" severity="success" text={translate("workspace-onboarding.subscription.free-eligible")}/>}
                            <WizardNavigation canGoBack={stepIndex > 0} onBack={goBack} onNext={goNext} nextDisabled={data.workspaceName.trim().length < 2}/>
                        </div>
                    </StepContent>
                );
            case "purpose":
                return (
                    <StepContent icon="pi pi-compass" title={translate("workspace-onboarding.purpose.title")} description={translate("workspace-onboarding.purpose.description")}>
                        <div className="flex flex-column gap-4">
                            <div className="grid align-items-stretch">
                                {PURPOSE_OPTIONS.map((option) => (
                                    <div key={option} className="col-12 md:col-4 flex">
                                        <OptionChoice layout="tile" id={`purpose-${option}`} name="purpose" value={option} selected={data.purpose === option} title={translate(`workspace-onboarding.purpose.${option}.title`)} description={translate(`workspace-onboarding.purpose.${option}.description`)} icon={option === "personal" ? "pi pi-user" : option === "projects" ? "pi pi-briefcase" : "pi pi-users"} onChange={(value) => setData({...data, purpose: value})}/>
                                    </div>
                                ))}
                            </div>
                            <WizardNavigation canGoBack onBack={goBack} onNext={goNext}/>
                        </div>
                    </StepContent>
                );
            case "owner-type":
                return (
                    <StepContent icon="pi pi-id-card" title={translate("workspace-onboarding.owner-type.title")} description={translate("workspace-onboarding.owner-type.description")}>
                        <div className="flex flex-column gap-4">
                            <SelectButton className="w-full flex ltr" pt={{button: {className: "flex-1"}}} value={data.ownerType} options={OWNER_TYPE_OPTIONS.map((value) => ({label: translate(`workspace-onboarding.owner-type.${value}`), value}))} optionLabel="label" optionValue="value" onChange={(event) => event.value && setData({...data, ownerType: event.value})}/>
                            <WizardNavigation canGoBack onBack={goBack} onNext={goNext}/>
                        </div>
                    </StepContent>
                );
            case "industry":
                return (
                    <StepContent icon="pi pi-chart-bar" title={translate("workspace-onboarding.industry.title")} description={translate("workspace-onboarding.industry.description")}>
                        <div className="flex flex-column gap-4">
                            <div className="grid">
                                {INDUSTRY_OPTIONS.map((option) => (
                                    <div key={option} className="col-12 sm:col-6 md:col-4 flex">
                                        <OptionChoice layout="tile" id={`industry-${option}`} name="industry" value={option} selected={data.workspaceCategory === option} title={translate(`workspace-onboarding.industry.${option}`)} icon={option === "tech-software" ? "pi pi-code" : option === "commerce" ? "pi pi-wallet" : option === "health-beauty-service" ? "pi pi-heart" : option === "enterprise-management" ? "pi pi-chart-line" : option === "education-media" ? "pi pi-graduation-cap" : "pi pi-ellipsis-h"} onChange={(value) => setData({...data, workspaceCategory: value})}/>
                                    </div>
                                ))}
                            </div>
                            <WizardNavigation canGoBack onBack={goBack} onNext={goNext}/>
                        </div>
                    </StepContent>
                );
            case "start":
                return (
                    <StepContent icon="pi pi-flag" title={translate("workspace-onboarding.start.title")} description={translate("workspace-onboarding.start.description")}>
                        <div className="flex flex-column gap-3">
                            {START_OPTIONS.map((option) => <OptionChoice key={option} id={`start-${option}`} name="starter" value={option} selected={data.starterType === option} title={translate(`workspace-onboarding.start.${option}.title`)} description={translate(`workspace-onboarding.start.${option}.description`)} icon={option === "project" ? "pi pi-folder-plus" : "pi pi-flag-fill"} onChange={(value) => setData({...data, starterType: value, starterTitle: "", starterDueDate: null})}/>)}
                            <WizardNavigation canGoBack onBack={goBack} onNext={goNext}/>
                        </div>
                    </StepContent>
                );
            case "starter-settings":
                return (
                    <StepContent icon={data.starterType === "project" ? "pi pi-folder" : "pi pi-flag"} title={translate(`workspace-onboarding.starter.${data.starterType}.title`)} description={translate(`workspace-onboarding.starter.${data.starterType}.description`)}>
                        <div className="flex flex-column gap-4">
                            <div className="flex flex-column gap-2"><label className="text-xs text-600">{translate(`workspace-onboarding.starter.${data.starterType}.name`)}</label><InputText className="w-full text-sm p-3" value={data.starterTitle} onChange={(event) => setData({...data, starterTitle: event.target.value})} placeholder={translate(`workspace-onboarding.starter.${data.starterType}.placeholder`)}/></div>
                            <div className="flex flex-column gap-2"><label className="text-xs text-600">{translate("workspace-onboarding.starter.due-date")}</label><PrimeDatePicker inputClassName="w-full" value={data.starterDueDate} onChange={(event) => setData({...data, starterDueDate: event.value})} placeholder={translate("workspace-onboarding.starter.due-date-placeholder")}/></div>
                            <WizardNavigation canGoBack onBack={goBack} onNext={goNext} nextDisabled={data.starterTitle.trim().length < 2}/>
                        </div>
                    </StepContent>
                );
            case "subscription":
                return (
                    <StepContent
                        icon="pi pi-verified"
                        title={translate(eligibility.freeSubscriptionEligible
                            ? "workspace-onboarding.subscription.first-workspace-title"
                            : "workspace-onboarding.subscription.title")}
                        description={translate(eligibility.freeSubscriptionEligible
                            ? "workspace-onboarding.subscription.first-workspace-description"
                            : "workspace-onboarding.subscription.description")}
                    >
                        <div className="flex flex-column gap-3">
                            <SubscriptionPlanSelector
                                plans={subscriptionPlans}
                                interval={subscriptionInterval}
                                selectedPlan={selectedSubscriptionPlan}
                                loading={false}
                                onIntervalChange={changeSubscriptionInterval}
                                onSelect={selectSubscriptionPlan}
                                translate={translate}
                                emptyText={translate("workspace-onboarding.subscription.empty")}
                                freeSelectionEnabled={eligibility.freeSubscriptionEligible}
                            />
                            {selectedSubscriptionPlan && activePlanCustomizations(selectedSubscriptionPlan).map((customization) => (
                                <div className="flex flex-column gap-2" key={customization.documentId}>
                                    <label className="text-xs font-bold" htmlFor={`onboarding-customization-${customization.documentId}`}>{customization.name}</label>
                                    {customization.selectionMode === "multiple" ? (
                                        <MultiSelect
                                            inputId={`onboarding-customization-${customization.documentId}`}
                                            className="w-full text-xs"
                                            value={data.selectedCustomizations?.[String(customization.documentId)] || []}
                                            options={(customization.options || []).filter((option) => option.isActive !== false)}
                                            optionLabel="name"
                                            optionValue="documentId"
                                            selectionLimit={Number(customization.maximumSelections || 0) || undefined}
                                            placeholder={translate("commerce.customization.select")}
                                            onChange={(event) => setData((current) => ({
                                                ...current,
                                                selectedCustomizations: {
                                                    ...current.selectedCustomizations,
                                                    [String(customization.documentId)]: event.value,
                                                },
                                            }))}
                                        />
                                    ) : (
                                        <Dropdown
                                            inputId={`onboarding-customization-${customization.documentId}`}
                                            className="w-full text-xs"
                                            value={data.selectedCustomizations?.[String(customization.documentId)] || null}
                                            options={(customization.options || []).filter((option) => option.isActive !== false)}
                                            optionLabel="name"
                                            optionValue="documentId"
                                            placeholder={translate("commerce.customization.select")}
                                            onChange={(event) => setData((current) => ({
                                                ...current,
                                                selectedCustomizations: {
                                                    ...current.selectedCustomizations,
                                                    [String(customization.documentId)]: event.value,
                                                },
                                            }))}
                                        />
                                    )}
                                </div>
                            ))}
                            {professionalSubscriptionSelected && <Button type="button" className="w-full text-xs gap-2" icon="pi pi-clock" severity="secondary" text label={translate("workspace-onboarding.action.pay-later")} onClick={deferProfessionalPayment} loading={submitting} disabled={!data.selectedPrice}/>}
                            {submitError && <Message className="w-full text-xs" severity="error" text={submitError}/>}
                            <WizardNavigation canGoBack onBack={goBack} onNext={goNext} nextDisabled={!data.selectedPrice || !customizationSelectionsComplete(selectedSubscriptionPlan || data.selectedPlan, data.selectedCustomizations)}/>
                        </div>
                    </StepContent>
                );
            case "invite":
                return (
                    <StepContent icon="pi pi-user-plus" title={translate("workspace-onboarding.invite.title")} description={translate("workspace-onboarding.invite.description")}>
                        <div className="flex flex-column gap-3">
                            {data.invites.map((invite, index) => (
                                <div key={index} className="grid align-items-end border-bottom-1 border-100 pb-2">
                                    <div className="col-12 md:col-5 flex flex-column gap-1"><label className="text-xs text-500">{translate("fullname")}</label><InputText className="w-full h-3rem text-xs p-2" value={invite.fullname} onChange={(event) => updateInvite(index, {fullname: event.target.value})} placeholder={translate("workspace-onboarding.invite.fullname-placeholder")}/></div>
                                    <div className="col-12 md:col-4 flex flex-column gap-1"><label className="text-xs text-500">{translate("mobile")}</label><InputText className="w-full h-3rem text-xs p-2 ltr text-left" value={invite.phonenumber} onChange={(event) => updateInvite(index, {phonenumber: String(farsiDigitsToInt(event.target.value) || "")})} placeholder={translate("workspace-onboarding.invite.mobile-placeholder")}/></div>
                                    <div className="col-12 md:col-3 flex flex-column gap-1"><label className="text-xs text-500">{translate("role")}</label><Dropdown className="w-full h-3rem text-xs rtl" panelClassName="rtl text-xs" pt={{input: {className: "flex align-items-center"}}} value={invite.role} options={INVITE_ROLE_OPTIONS.map((value) => ({label: translate(value === "viewer" ? "workspace-member" : "guest"), value}))} onChange={(event) => updateInvite(index, {role: event.value})}/></div>
                                </div>
                            ))}
                            {!activeInvites.length && <Message className="w-full text-xs" severity="info" text={translate("workspace-onboarding.invite.optional")}/>}
                            {submitError && <Message className="w-full text-xs" severity="error" text={submitError}/>}
                            <WizardNavigation canGoBack={!createdWorkspace} onBack={goBack} onNext={provisionWorkspace} nextLabel={translate(professionalSubscriptionSelected ? "workspace-onboarding.action.create-and-checkout" : "workspace-onboarding.action.finish")} loading={busy}/>
                        </div>
                    </StepContent>
                );
            default:
                return null;
        }
    };

    const wideStep = ["purpose", "industry", "subscription"].includes(currentStep);

    return (
        <div className="w-full h-full flex flex-column rtl surface-0 overflow-hidden">
            <header className="w-full flex align-items-center justify-content-between gap-3 px-3 md:px-5 py-4 border-bottom-1 border-100">
                <BrandLogo className="w-10rem sm:w-12rem h-auto"/>
                <div className="flex align-items-center gap-3">
                    <span className="hidden sm:inline text-xs text-500">{translate("workspace-onboarding.progress").replace("{current}", displayStep).replace("{total}", totalSteps)}</span>
                    <ProgressBar className="w-8rem sm:w-10rem h-1rem" value={(displayStep / totalSteps) * 100} showValue={false}/>
                    {closable && <Button type="button" icon="pi pi-times" aria-label={translate("workspace-onboarding.action.close")} severity="secondary" text rounded onClick={onClose}/>}
                </div>
            </header>
            <main className="flex-1 overflow-auto flex align-items-center justify-content-center p-3 md:p-5">
                <form className={`w-full ${wideStep ? "xl:w-11" : "lg:w-8 xl:w-6"}`} onSubmit={handleWizardSubmit}>{renderStep()}</form>
            </main>
        </div>
    );
}

export function CreateWorkspace({onCreated, welcomeStep = false, closable = false, onClose}) {
    return <WorkspaceOnboardingWizard onCreated={onCreated} welcomeStep={welcomeStep} closable={closable} onClose={onClose}/>;
}

export function Init({onCreated, closable = false, onClose}) {
    return (
        <div className="w-full h-full overflow-hidden surface-0">
            <CreateWorkspace onCreated={onCreated} welcomeStep closable={closable} onClose={onClose}/>
        </div>
    );
}

export default Init;
